import type { UseIntroVideoResult } from '@belong/common';
import { IntroVideo, Text } from '@belong/ui';
import { clsx } from 'clsx';
import { formatString } from 'strings';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import styles from './intro-video-cta.module.css';

type Props = {
  video: UseIntroVideoResult;
  onContinue: () => void;
  ctaLabel: string;
};

type VideoCtaProps = Props & {
  videoSrc: string;
};

function WatchCountdown({ remainingTime }: { remainingTime: string }) {
  return (
    <Text variant="p1">
      {POST_INSPECTION_FLOW_STRINGS['intro.video.countdown']}{' '}
      <Text as="span" fontWeight="semibold" variant="p1">
        {formatString(
          POST_INSPECTION_FLOW_STRINGS['intro.video.countdown_remaining'],
          { remainingTime },
        )}
      </Text>
    </Text>
  );
}

export function IntroVideoCta({
  video,
  onContinue,
  ctaLabel,
  videoSrc,
}: VideoCtaProps) {
  const {
    isUnlocked: hasWatchedVideo,
    progress,
    remainingTime,
    showCountdown,
  } = video;

  return (
    <>
      <IntroVideo video={video} videoSrc={videoSrc} className="mb-xl" />

      <div className="mb-3xl hidden flex-col items-center gap-xs md:flex">
        <button
          className={styles.progressButton}
          onClick={() => onContinue()}
          disabled={!hasWatchedVideo}
        >
          <div
            className={styles.progressFill}
            style={{ width: `${hasWatchedVideo ? 100 : progress}%` }}
          />
          <Text fontWeight="semibold" className="relative z-10 text-white">
            {ctaLabel}
          </Text>
        </button>
        {!hasWatchedVideo && showCountdown && (
          <WatchCountdown remainingTime={remainingTime} />
        )}
      </div>
    </>
  );
}

export function IntroMobileCta({ video, onContinue, ctaLabel }: Props) {
  const { isUnlocked: hasWatchedVideo, progress, remainingTime } = video;

  return (
    <div className="my-2xl w-full md:hidden">
      <div className={clsx('py-xs text-center', styles.mobileTimerBg)}>
        <WatchCountdown remainingTime={remainingTime} />
      </div>
      <button
        className={styles.mobileProgressButton}
        onClick={() => onContinue()}
        disabled={!hasWatchedVideo}
      >
        <div
          className={styles.progressFill}
          style={{ width: `${hasWatchedVideo ? 100 : progress}%` }}
        />
        <Text fontWeight="semibold" className="relative z-10 text-white">
          {ctaLabel}
        </Text>
      </button>
    </div>
  );
}
